import { useState, useEffect } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import Create from "./Create";
import "./design/List.css"; // Archivo CSS
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus, faTrash, faPenToSquare, faFileLines, faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { RoleGate } from "./middleware/RoleGate";

function SociosTable() {
    const [socios, setSocios] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: "n_socio", direction: "asc" });
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [sociosPerPage, setSociosPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [familiaFiltro, setFamiliaFiltro] = useState("");
    const [statusFiltro, setStatusFiltro] = useState("");
    const [tipoFiltro, setTipoFiltro] = useState("");
    const [showModal, setShowModal] = useState(false);

    // Obtener socios
    useEffect(() => {
        fetchSocios();
    }, []);
    // Función para obtener socios desde la API
    const fetchSocios = async () => {
        try {
            setLoading(true);
            const response = await axios.get("https://socios.kolpingchile.cl/backend/api/socios");
            const sociosData = response.data;
            setSocios(
                sociosData.sort((a, b) => a.n_socio - b.n_socio) // Ordenar por n_socio al cargar
            );
        } catch (error) {
            console.error("Error al obtener socios", error);
        } finally {
            setLoading(false);
        }
    };
    const handleCloseModal = () => { setShowModal(false); fetchSocios(); }

    const rutConGuion = (rut) => {
        if (!rut) return "";

        // Quitar puntos y guion
        let rutLimpio = rut.replace(/\./g, "").replace(/-/g, "");

        // Separar cuerpo y dígito verificador
        const cuerpo = rutLimpio.slice(0, -1);
        const dv = rutLimpio.slice(-1);

        // Formatear con puntos cada 3 dígitos (de derecha a izquierda)
        let cuerpoFormateado = "";
        let contador = 0;

        for (let i = cuerpo.length - 1; i >= 0; i--) {
            cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
            contador++;
            if (contador % 3 === 0 && i !== 0) {
                cuerpoFormateado = "." + cuerpoFormateado;
            }
        }

        return `${cuerpoFormateado}-${dv.toUpperCase()}`; // Retornar con guion y DV en mayúscula
    };
    // Manejar eliminación de socio
    const handleDelete = async (rut) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar este socio?")) return;

        try {
            await axios.delete(`https://socios.kolpingchile.cl/backend/api/socios/${rut}`);
            fetchSocios(); // Actualizar lista
        } catch (error) {
            console.error('Error al eliminar socio:', error.response?.data || error.message);
        }
    };
    // Manejar ordenamiento de socios
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
        }));
    };
    // Filtrar y ordenar socios

    const sortedSocios = [...socios]
        .filter((socio) => {
            const fullName = `${socio.nombre} ${socio.apellido}`.toLowerCase();
            const rut = socio.rut.toLowerCase();
            const n_socio = socio.n_socio.toString();
            const matchesSearch = (
                fullName.includes(searchTerm.toLowerCase()) ||
                rut.includes(searchTerm.toLowerCase()) ||
                n_socio.includes(searchTerm.toLowerCase())
            );

            const matchesFamilia = familiaFiltro === "" || socio.nombreFamilia === familiaFiltro;
            const matchesStatus = statusFiltro === "" || String(socio.status) === statusFiltro;
            const matchesTipo = tipoFiltro === "" || String(socio.tipo_socio) === tipoFiltro;

            return matchesSearch && matchesFamilia && matchesStatus && matchesTipo;
        })
        .sort((a, b) => {
            if (!sortConfig.key) return 0;

            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (sortConfig.key === "nombre") {
                aValue = `${a.nombre} ${a.apellido}`.toLowerCase();
                bValue = `${b.nombre} ${b.apellido}`.toLowerCase();
            }

            if (sortConfig.direction === "asc") {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
        
    const indexOfLastSocio = currentPage * sociosPerPage;
    const indexOfFirstSocio = indexOfLastSocio - sociosPerPage;
    const currentSocios = sortedSocios.slice(indexOfFirstSocio, indexOfLastSocio);
    const totalPages = Math.ceil(sortedSocios.length / sociosPerPage);
    // Obtener números de página visibles
    const getVisiblePageNumbers = () => {
        const maxPagesToShow = 5;
        let startPage = Math.max(currentPage - Math.floor(maxPagesToShow / 2), 1);
        let endPage = startPage + maxPagesToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(endPage - maxPagesToShow + 1, 1);
        }

        return [...Array(endPage - startPage + 1)].map((_, idx) => startPage + idx);
    };
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status" style={{ width: "4rem", height: "4rem" }}>
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="text-center">Cargando socios, por favor espera...</p>
                </div>
            </div>
        );
    }
    // Función para exportar a Excel
    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(currentSocios);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Socios");
        XLSX.writeFile(wb, "socios.xlsx");
    };
    // Función para exportar a PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Lista de Socios", 10, 10);
        doc.autoTable({ html: "#socios-table" });
        doc.save("socios.pdf");
    };
    // Renderizar la tabla de socios
    return (
        <div className="container mt-5">
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                </Modal.Header>
                <Modal.Body>
                    <Create onSuccess={handleCloseModal} />
                </Modal.Body>
            </Modal>

            <div className="card shadow-lg">
                <div className="card-body">
                    <h3 className="mt-4 text-center">Lista de Socios</h3>
                    <RoleGate allowedRoles={["Administrador", "Digitador"]}>
                        <button className="btn mb-3" onClick={() => setShowModal(true)}>
                            <FontAwesomeIcon icon={faCirclePlus} size="2x" />
                            <div>Nuevo Socio</div>
                        </button>
                    </RoleGate>
                    {/* Filtro de búsqueda */}
                    <div className="row mb-4">
                        <div className="col-md-3 mb-2">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="BUSCAR POR N° SOCIO, RUT O NOMBRE"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3 mb-2">
                            <select
                                className="form-select"
                                value={familiaFiltro}
                                onChange={(e) => setFamiliaFiltro(e.target.value)}
                            >
                                <option value="">Todas las familias</option>
                                {[...new Set(socios.map(s => s.nombreFamilia))]
                                    .filter(f => f !== "Desconocido")
                                    .sort((a, b) => {
                                        const valorA = a ?? "";
                                        const valorB = b ?? "";
                                        return valorA.localeCompare(valorB, "es", { sensitivity: "base" });
                                    })

                                    .map((familia, idx) => (
                                        <option key={idx} value={familia}>{familia}</option>
                                    ))}

                            </select>
                        </div>

                        <div className="col-md-2 mb-2">
                            <select
                                className="form-select"
                                value={tipoFiltro}
                                onChange={(e) => setTipoFiltro(e.target.value)}
                            >
                                <option value="">Todos los tipos</option>
                                <option value="1">ACTIVO</option>
                                <option value="2">PASIVO</option>
                                <option value="3">COOPERADOR</option>
                                <option value="4">ESTUDIANTE</option>
                            </select>
                        </div>
                        <div className="col-md-2 mb-2">
                            <select
                                className="form-select"
                                value={statusFiltro}
                                onChange={(e) => setStatusFiltro(e.target.value)}
                            >
                                <option value="">Todos los estados</option>
                                <option value="1">ACTIVO</option>
                                <option value="2">INACTIVO</option>
                                <option value="3">SUSPENDIDO</option>
                                <option value="4">FALLECIDO</option>
                                <option value="5">EXENTO</option>
                            </select>
                        </div>
                        <div className="col mb-2">
                            <button className="btn btn-success" onClick={exportToExcel}>
                                <FontAwesomeIcon icon={faFileExcel} size="lg" />
                            </button>
                        </div>
                        <div className="col mb-2">
                            <button className="btn btn-danger" onClick={exportToPDF}>
                                <FontAwesomeIcon icon={faFilePdf} size="lg" />
                            </button>
                        </div>
                    </div>

                    <div className="mb-4">
                        <div><label className="mr-2 fw-medium">N° de filas:  </label>
                            <select
                                value={sociosPerPage}
                                onChange={(e) => {
                                    setCurrentPage(1);
                                    setSociosPerPage(Number(e.target.value));
                                }}
                                className="border rounded px-2 py-1"
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>

                    <p className="text-center text-muted">
                        Mostrando {indexOfFirstSocio + 1}–{Math.min(indexOfLastSocio, sortedSocios.length)} de {sortedSocios.length} Socios Kolping
                    </p>


                    <div className="table-responsive">
                        <table className="table table-bordered table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>
                                        <button className="btn btn-sm kolping-button" onClick={() => handleSort("n_socio")}>
                                            N° Socio {sortConfig.key === "n_socio" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                                        </button>
                                    </th>
                                    <th>
                                        <button className="btn btn-sm me-2 kolping-button" onClick={() => handleSort("rut")}>
                                            RUT {sortConfig.key === "rut" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                                        </button>
                                    </th>
                                    <th>
                                        <button className="btn btn-sm me-3 kolping-button" onClick={() => handleSort("nombre")}>
                                            Nombre {sortConfig.key === "nombre" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                                        </button>
                                    </th>
                                    <th>
                                        <button className="btn btn-sm me-2 kolping-button" onClick={() => handleSort("familia")}>
                                            Familia {sortConfig.key === "familia" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                                        </button>
                                    </th>
                                    <th>
                                        <button className="btn btn-sm me-2 kolping-button" onClick={() => handleSort("tipo_socio")}>
                                            Tipo Socio {sortConfig.key === "tipo_socio" ? (sortConfig.direction === "asc" ? "▲" : "▼") : ""}
                                        </button>
                                    </th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentSocios.map((socio) => (
                                    <tr key={socio.rut}>
                                        <td>{socio.n_socio}</td>
                                        <td>{rutConGuion(socio.rut)}</td>
                                        <td>{socio.nombre} {socio.apellido}</td>
                                        <td>{socio.nombreFamilia}</td>
                                        <td>
                                            <span className={`badge bg-${String(socio.tipo_socio) === "1" ? "success" : String(socio.tipo_socio) === "2" ? "secondary" : String(socio.tipo_socio) === "3" ? "info" : "warning"}`}>
                                                {String(socio.tipo_socio) === "1" ? "ACTIVO" : String(socio.tipo_socio) === "2" ? "PASIVO" : String(socio.tipo_socio) === "3" ? "COOPERADOR" : "ESTUDIANTE"}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: "small", width: "1%" }}>
                                            <span className={`badge bg-${String(socio.status) === "1" ? "success" : String(socio.status) === "2" ? "danger" : String(socio.status) === "3" ? "warning" : String(socio.status) === "4" ? "dark" : "info"}`}>
                                                {String(socio.status) === "1" ? "ACTIVO" : String(socio.status) === "2" ? "INACTIVO" : String(socio.status) === "3" ? "SUPENDIDO" : String(socio.status) === "4" ? "FALLECIDO" : "EXENTO"}
                                            </span>
                                        </td>
                                        <td style={{ width: "14%" }}>
                                            <Link className="btn btn-sm kolping-button2" title="Ver Ficha" to={`/ficha/${socio.rut}`}>
                                                <FontAwesomeIcon icon={faFileLines} size="lg" />
                                            </Link>
                                            <RoleGate allowedRoles={["Administrador"]}>
                                                <Link className="btn btn-sm kolping-button2" title="Editar" to={`/editar/${socio.rut}`}>
                                                    <FontAwesomeIcon icon={faPenToSquare} size="lg" />
                                                </Link>
                                                <button className="btn btn-sm kolping-button2" title="Eliminar" onClick={() => handleDelete(socio.rut)}>
                                                    <FontAwesomeIcon icon={faTrash} size="lg" />
                                                </button>
                                            </RoleGate>
                                        </td>

                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="text-center text-muted">
                            Mostrando {indexOfFirstSocio + 1}–{Math.min(indexOfLastSocio, sortedSocios.length)} de {sortedSocios.length} Socios
                        </p>
                        <div className="d-flex justify-content-right mt-3">
                            <nav>
                                <div className="d-flex justify-content-right mt-3">
                                    <nav>
                                        <ul className="pagination">

                                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                                                    Anterior
                                                </button>
                                            </li>
                                            {getVisiblePageNumbers().map((number) => (
                                                <li key={number} className={`page-item ${currentPage === number ? "active" : ""}`}>
                                                    <button className="page-link" onClick={() => setCurrentPage(number)}>
                                                        {number}
                                                    </button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                                                    Siguiente
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SociosTable;
